import { useEffect as useLayoutEffectOnce } from "react";
import GoogleReviewsMarquee from "@/Components/GoogleReviewsMarquee";
import { usePage as useInertiaPage } from "@inertiajs/react";
import React, { useEffect } from "react";
import Header from "./Header/Header";
import Footer from "./Footer/Footer";
import ContactWhats from "@/Components/ContactWhats";
import Breadcrumbs from "@/Components/Breadcrumbs";
import SeoHead from "@/Components/SeoHead";

export default function WebsiteLayout({ children }) {
    const { url: pageUrl = "" } = useInertiaPage();
    const isServicePage = decodeURIComponent(pageUrl.split("?")[0]).startsWith("/services/");

    // Any full-page website screenshot (much taller than wide, cropped with
    // object-cover) auto-scrolls from the hero to the footer and back, on
    // every page — so visitors see the whole site without clicking.
    useLayoutEffectOnce(() => {
        const mark = (img) => {
            if (!(img instanceof HTMLImageElement) || img.classList.contains("wz-site-scroll")) return;
            const check = () => {
                if (img.naturalHeight > img.naturalWidth * 2 && getComputedStyle(img).objectFit === "cover") {
                    img.classList.add("wz-site-scroll");
                }
            };
            if (img.complete) check(); else img.addEventListener("load", check, { once: true });
        };
        document.querySelectorAll("main img").forEach(mark);
        const mo = new MutationObserver((list) => list.forEach((m) => m.addedNodes.forEach((n) => {
            if (n.nodeType !== 1) return;
            if (n.tagName === "IMG") mark(n); else n.querySelectorAll?.("img").forEach(mark);
        })));
        mo.observe(document.body, { childList: true, subtree: true });
        return () => mo.disconnect();
    }, []);
    const whatsappNumber = "+966569691027"; // Replace with your actual WhatsApp number
    const whatsappMessage = "مرحباً، أود الاستفسار عن خدماتكم"; // Default message

    useEffect(
        function () {
            // scroll to top on page load
            window.scrollTo({ top: 0, behavior: "smooth" });
        },
        [children],
    );

    return (
        <div id="website-shell" className="flex min-h-screen flex-col">
            {/* Renders page-specific JSON-LD schemas from seo.schema prop */}
            <SeoHead />
            <Header />

            {/* Single source of truth for the gap below the fixed header —
                every page's content (breadcrumbs, then hero) starts at this
                exact offset. Header height + breathing space per breakpoint:
                mobile 72+36+52(ticker+gap)=160px, tablet 76+44=120px, desktop
                88+56=144px — the promo ticker above the nav is mobile-only,
                so only the mobile offset grew. Individual pages/heroes must
                not add their own top padding or margin to clear the header —
                that's what caused the inconsistency this replaces. */}
            <div className="flex-grow pt-[160px] md:pt-[120px] lg:pt-[144px]">
                <Breadcrumbs />
                <style>{`.wz-site-scroll{object-position:50% 0%;animation:wz-site-scroll 12s ease-in-out infinite alternate}@keyframes wz-site-scroll{0%,10%{object-position:50% 0%}90%,100%{object-position:50% 100%}}`}</style>
                <main>{children}</main>
                {isServicePage && <GoogleReviewsMarquee atPageEnd />}
            </div>
            <Footer />

            {/* Floating WhatsApp Button — simple flat circle, no pulsing
                ring/glow halo, sized to the standard 52-56px floating-action
                convention instead of a large hype-style bubble. Anchored on
                ONE side only: website.blade.php also sets right:24px, and
                left + right together stretched this button into an invisible
                full-width strip that swallowed clicks along the viewport
                bottom (footer socials opened WhatsApp). */}
            <ContactWhats>
                <button
                    className="whatsapp-float-btn fixed bottom-[max(20px,env(safe-area-inset-bottom))] right-6 z-50 group transition-[bottom] duration-300 ease-out"
                    aria-label="تواصل عبر واتساب"
                >
                    <div className="relative w-12 h-12 sm:w-14 sm:h-14 bg-[#25D366] rounded-full shadow-lg flex items-center justify-center transition-transform duration-300 hover:scale-105 hover:bg-green-600">
                        {/* WhatsApp Icon SVG */}
                        <svg
                            className="w-6 h-6 sm:w-7 sm:h-7 text-white"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                        >
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                        </svg>
                    </div>

                    {/* Tooltip */}
                    <div className="whatsapp-tooltip absolute bottom-full left-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                        <div className="bg-gray-900 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap shadow-lg">
                            تواصل عبر واتساب
                            <div className="absolute top-full left-4 -mt-1">
                                <div className="border-4 border-transparent border-t-gray-900"></div>
                            </div>
                        </div>
                    </div>
                </button>
            </ContactWhats>
        </div>
    );
}
